import { FC, memo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Home, Search, Plus, Inbox, User } from 'lucide-react';
import styled from 'styled-components';
import Lottie from 'lottie-react';

import { APP_ROUTES, AUTH_ROUTES } from '@navigation/types';
import theme from 'theme';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useUnauthorizedModal } from '@hooks/useUnauthorizedModal';
import { useAuthUser } from '@redux/selectors/auth';
import { useCurrentUser } from '@redux/selectors/users';
import { BOTTOM_MENU_HEIGHT } from '@utils/constants';
import { PremierText } from '@components/text';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import animationData from '@assets/animations/home.json';

const Container = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 16px;
  height: ${BOTTOM_MENU_HEIGHT}px;
  background-color: ${({ theme }) => theme.colors.black};
  border-top: 1px solid ${({ theme }) => theme.colors.grey[800]};
  z-index: 1000;
`;

const MenuButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.white : theme.colors.grey[400]};
  transition: color 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const LogoButton = styled(TouchableOpacity)`
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Animation = styled(Lottie)`
  position: absolute;
  width: 64px;
  height: 64px;
`;

const Label = styled(PremierText)<{ $isActive?: boolean }>`
  margin-top: 4px;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.white : theme.colors.grey[400]};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
`;

const enum BottomMenuTab {
  HOME = '/',
  SEARCH = '/search',
  INBOX = '/inbox',
  PROFILE = '/profile',
}

const ICON_SIZE = 24;
const PLUS_ICON_SIZE = 28;
const ANIMATION_INITIAL_SEGMENT: [number, number] = [65, 200];

type Props = {
  onSideMenuClose?: () => void;
};

const BottomMenu: FC<Props> = ({ onSideMenuClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAnonymous } = useAuthUser();
  const currentUser = useCurrentUser();
  const { openUnauthorizedModal } = useUnauthorizedModal();
  const { openDownloadAppModal } = useDownloadAppModal();

  const getIsActive = (path: string) => {
    if (path === BottomMenuTab.HOME) {
      return location.pathname === BottomMenuTab.HOME;
    }
    if (path === BottomMenuTab.PROFILE) {
      const profilePath = `${APP_ROUTES.PROFILE}/${currentUser?.id}`;
      return location.pathname === profilePath;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = useCallback(
    (path: string) => {
      if (path === BottomMenuTab.SEARCH && isAnonymous) {
        openUnauthorizedModal();
        return;
      }

      if (path === BottomMenuTab.INBOX) {
        openDownloadAppModal();
        return;
      }
      onSideMenuClose?.();
      navigate(path);
    },
    [
      isAnonymous,
      navigate,
      openUnauthorizedModal,
      openDownloadAppModal,
      onSideMenuClose,
    ],
  );

  const handleGoLive = useCallback(() => {
    // TODO:Add go live logic here
    openDownloadAppModal();
    onSideMenuClose?.();
  }, [openDownloadAppModal, onSideMenuClose]);

  const handleProfile = useCallback(() => {
    if (isAnonymous) {
      onSideMenuClose?.();
      navigate(AUTH_ROUTES.LOGIN);
      return;
    }
    onSideMenuClose?.();
    navigate(`${APP_ROUTES.PROFILE}/${currentUser?.id}`);
  }, [isAnonymous, navigate, onSideMenuClose, currentUser?.id]);

  return (
    <>
      <Container>
        <MenuButton
          $isActive={getIsActive(BottomMenuTab.HOME)}
          onClick={() => handleNavigation(APP_ROUTES.HOME)}
        >
          <Home size={ICON_SIZE} />
          <Label $isActive={getIsActive(BottomMenuTab.HOME)}>HOME</Label>
        </MenuButton>

        <MenuButton
          $isActive={getIsActive(BottomMenuTab.SEARCH)}
          onClick={() => handleNavigation(BottomMenuTab.SEARCH)}
        >
          <Search size={ICON_SIZE} />
          <Label $isActive={getIsActive(BottomMenuTab.SEARCH)}>SEARCH</Label>
        </MenuButton>

        <LogoButton onClick={handleGoLive}>
          <Animation
            animationData={animationData}
            autoplay
            loop
            initialSegment={ANIMATION_INITIAL_SEGMENT}
          />
          <Plus size={PLUS_ICON_SIZE} color={theme.colors.white} />
        </LogoButton>

        <MenuButton
          $isActive={getIsActive(BottomMenuTab.INBOX)}
          onClick={() => handleNavigation(BottomMenuTab.INBOX)}
        >
          <Inbox size={ICON_SIZE} />
          <Label $isActive={getIsActive(BottomMenuTab.INBOX)}>INBOX</Label>
        </MenuButton>

        <MenuButton
          $isActive={getIsActive(BottomMenuTab.PROFILE)}
          onClick={handleProfile}
        >
          <User size={ICON_SIZE} />
          <Label $isActive={getIsActive(BottomMenuTab.PROFILE)}>PROFILE</Label>
        </MenuButton>
      </Container>
    </>
  );
};

export default memo(BottomMenu);
